<template>
  <div class="edit-customer-view">
    <!-- Back header -->
    <div class="view-header">
      <button class="back-btn" @click="$emit('back')">
        <i class="tim-icons icon-minimal-left"></i>
      </button>
      <h5 class="view-title mb-0">Edit customer</h5>
    </div>

    <div class="view-body">
      <!-- Customer lookup -->
      <div class="edit-field">
        <label class="field-label text-white">Customer lookup</label>
        <base-input>
        <el-autocomplete
          style="width: 100%"
          v-model="lookupValue"
          :fetch-suggestions="querySearchCustomer"
          placeholder="Search by name, email, or phone"
          :trigger-on-focus="false"
          @select="handleSelectCustomer"
        >
          <template #default="{ item }">
            <p style="margin-bottom: -12px" class="text-primary">{{ item.first_name }} {{ item.last_name }}</p>
            <small class="font-italic">
              <i class="tim-icons icon-email-85"></i> {{ item.email }}
              <span v-if="item.phone">
                <i class="tim-icons el-icon-mobile-phone ml-2"></i> {{ formatPhone(item.phone) }}
              </span>
            </small>
          </template>
          <template #append>
            <button class="lookup-clear-append" type="button" @click="clearLookup">
              <i class="tim-icons icon-refresh-02"></i>
            </button>
          </template>
        </el-autocomplete>
        </base-input>
      </div>

      <!-- Name fields -->
      <div class="edit-field">
        <div class="d-flex" style="gap: 8px;">
          <div class="flex-1">
            <label class="field-label text-white">First name *</label>
            <base-input v-model="form.first_name" placeholder="First name" />
          </div>
          <div class="flex-1">
            <label class="field-label text-white">Last name *</label>
            <base-input v-model="form.last_name" placeholder="Last name" />
          </div>
        </div>
      </div>

      <!-- Email -->
      <div class="edit-field">
        <label class="field-label text-white">Email *</label>
        <base-input v-model="form.email" placeholder="Email" :error="form.email && !isEmailValid ? 'Please enter a valid email address' : ''" />
      </div>

      <!-- Phone -->
      <div class="edit-field">
        <label class="field-label text-white">{{ isPhoneRequired() ? 'Phone *' : 'Phone (optional)' }}</label>
        <base-input type="text">
          <phone-number-input
            v-model="form.phone"
            :default-country="countryCode"
            @phone-update="handlePhoneUpdate"
          />
        </base-input>
      </div>

      <!-- Error message -->
      <div v-if="errorMessage" class="edit-error mt-3">
        <i class="tim-icons icon-alert-circle-exc mr-1"></i>
        {{ errorMessage }}
      </div>
    </div>

    <!-- Footer -->
    <div class="view-footer">
      <base-button size="sm" type="default" @click="$emit('back')">Cancel</base-button>
      <base-button
        size="sm"
        type="primary"
        :loading="saving"
        :disabled="!canSave"
        @click="save"
      >
        Save
      </base-button>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { API_LOCATION } from '@/api-config';
import { Auth } from 'aws-amplify';
import { Autocomplete } from 'element-ui';
import PhoneNumberInput from '@/components/PhoneNumberInput.vue';
import { formatPhoneDisplay } from '@/util/phoneFormatter';
import { isValidEmail } from '@/util/emailValidator';
import { isInternalPhoneRequired, validateInternalCustomerPhone } from '@/util/customerFieldValidation';

export default {
  name: 'EditCustomerView',

  components: {
    [Autocomplete.name]: Autocomplete,
    PhoneNumberInput
  },

  props: {
    booking: { type: Object, required: true },
    companyId: { type: [String, Number], required: true },
    companyGroupId: { type: [String, Number], required: true },
    countryCode: { type: String, default: 'US' }
  },

  data() {
    return {
      lookupValue: '',
      form: {
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        phoneE164: ''
      },
      saving: false,
      errorMessage: ''
    };
  },

  computed: {
    isEmailValid() {
      return isValidEmail(this.form.email);
    },
    canSave() {
      if (!this.form.first_name || !this.form.last_name) return false;
      if (!this.isEmailValid) return false;
      if (!this.isPhoneValid) return false;
      return !this.saving;
    },
    isPhoneValid() {
      try {
        return validateInternalCustomerPhone(this.form, JSON.parse(localStorage.getItem('group') || '{}'));
      } catch (e) {
        return true;
      }
    }
  },

  mounted() {
    this.form.first_name = this.booking.customer_first_name || '';
    this.form.last_name = this.booking.customer_last_name || '';
    this.form.email = this.booking.customer_email || '';
    this.form.phone = this.booking.customer_phone || '';
  },

  methods: {
    isPhoneRequired() {
      try {
        return isInternalPhoneRequired(JSON.parse(localStorage.getItem('group') || '{}'));
      } catch (e) {
        return false;
      }
    },

    async getAuthToken() {
      const session = await Auth.currentSession();
      return session.getIdToken().getJwtToken();
    },

    async querySearchCustomer(queryString, cb) {
      if (!queryString || queryString.length < 2) { cb([]); return; }
      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/customers?count=10000000&offset=0&first_name=&last_name=&email=&phone=&game=&tag=&autocomplete=${encodeURIComponent(queryString)}`;
        const response = await axios.get(url, config);
        cb(response.data.customers || []);
      } catch (error) {
        console.error('Error searching customers:', error);
        cb([]);
      }
    },

    handleSelectCustomer(item) {
      this.form.first_name = item.first_name || '';
      this.form.last_name = item.last_name || '';
      this.form.email = item.email || '';
      this.form.phone = item.phone || '';
      this.lookupValue = `${item.first_name} ${item.last_name}`;
    },

    clearLookup() {
      this.lookupValue = '';
    },

    formatPhone(phone) {
      return formatPhoneDisplay(phone, this.countryCode);
    },

    handlePhoneUpdate(phoneData) {
      // Store raw digits for saving
      if (phoneData && phoneData.raw) {
        this.form.phone = phoneData.raw;
      }
      this.form.phoneE164 = (phoneData && phoneData.e164) || '';
    },

    async save() {
      if (!this.canSave) return;

      this.saving = true;
      this.errorMessage = '';

      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/transactions/${this.booking.transaction_id}`;
        await axios.put(url, this.form, config);
        this.$emit('updated');
      } catch (error) {
        console.error('Failed to update customer:', error);
        this.errorMessage = error.response?.data?.message || error.response?.data || 'Failed to update customer';
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style scoped lang="scss">
.edit-customer-view {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.view-header {
  display: flex;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  gap: 12px;
}

.back-btn {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  padding: 4px;
  font-size: 14px;
  &:hover { color: #fff; }
}

.view-title {
  color: #fff;
  font-size: 15px;
  font-weight: 600;
}

.view-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}

.edit-field {
  margin-bottom: 16px;
}

.field-label {
  display: block;
  font-size: 12px;
  margin-bottom: 6px;
  font-weight: 500;
}

.flex-1 { flex: 1; }

.edit-error {
  color: #F5365C;
  font-size: 13px;
  padding: 10px 12px;
  background: rgba(245, 54, 92, 0.1);
  border-radius: 6px;
}

.view-footer {
  display: flex;
  justify-content: flex-end;
  padding: 14px 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  gap: 8px;
}

.lookup-clear-append {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
  padding: 4px 8px;
  font-size: 13px;

  &:hover { color: #fff; }
}
</style>

<style lang="scss">
.white-content {
  .edit-customer-view {
    .view-header {
      border-color: #e3e3e3;
    }

    .view-title {
      color: #1E1E2F;
    }

    .back-btn {
      color: rgba(0, 0, 0, 0.5);
      &:hover { color: #1E1E2F; }
    }

    .view-footer {
      border-color: #e3e3e3;
    }

    .field-label.text-white {
      color: #525f7f !important;
    }

    .lookup-clear-append {
      color: rgba(0, 0, 0, 0.5);
      &:hover { color: #1E1E2F; }
    }
  }
}
</style>
